/* eslint-disable max-classes-per-file */
import { RangeSetBuilder } from '@codemirror/state';
import {
	Decoration, EditorView, ViewPlugin, ViewUpdate, WidgetType,
} from '@codemirror/view';

// markers can be used at positions on a line over a range
const indentationMark = Decoration.mark({ class: 'cm-indentation-guide' });

// widget are used for space only lines when we need to fill
// the indentation guides on parts of the line that don't exist
class IndentationWidget extends WidgetType {
	/**
	 *
	 * @param {number[]} indents
	 */
	static create(indents) {
		return Decoration.widget({
			widget: new IndentationWidget(indents),
			side: 1,
		});
	}

	/**
	 *
	 * @param {number[]} indents
	 */
	constructor(indents) {
		super();
		this.indents = indents;
	}

	toDOM() {
		const wrap = document.createElement('span');
		wrap.className = 'cm-indentation-widget';
		for (const indent of this.indents) {
			// eslint-disable-next-line unicorn/prefer-dom-node-append
			const marker = wrap.appendChild(document.createElement('span'));
			marker.className = 'cm-indentation-guide';
			marker.textContent = ' '.repeat(indent);
		}
		return wrap;
	}
}

const SPACES = /^\s*/;

/**
 *
 * @param {string} text
 * @returns
 */
function getCodeStart(text) {
	return text.match(SPACES)[0].length;
}

/**
 *
 * @param {number} from
 * @param {number} to
 * @param {number} indent
 * @param {number} tabSize
 * @param {RangeSetBuilder<Decoration>} builder
 */
function makeIndentationMark(
	from,
	to,
	indent,
	tabSize,
	builder,
) {
	let indentPos = from + 2 * tabSize;
	const length = to - from;
	const upTo = Math.min(to - (length % tabSize), from + indent);
	while (indentPos <= upTo) {
		builder.add(indentPos - 1, indentPos, indentationMark);
		indentPos += tabSize;
	}
}

/**
 *
 * @param {number} from
 * @param {number} to
 * @param {number} indent
 * @param {number} tabSize
 * @param {RangeSetBuilder<Decoration>} builder
 */
function makeIndentationWidget(
	from,
	to,
	indent,
	tabSize,
	builder,
) {
	const length = to - from;
	if (indent <= length) {
		// we only add widgets when the line length is less than the indentation guide
		// if the indent <= length we just use the indentationMark
		return;
	}
	const indents = [];
	const toFill = indent - length;
	let initialFill = toFill % tabSize;
	if (length < tabSize) {
		initialFill += length ? tabSize : 2 * tabSize;
	}
	if (initialFill) {
		indents.push(initialFill);
	}
	const quotient = (toFill - initialFill) / tabSize;
	// eslint-disable-next-line unicorn/no-new-array
	indents.push(...new Array(quotient).fill(tabSize));
	builder.add(to, to, IndentationWidget.create(indents));
}

/**
 *
 * @param {EditorView} view
 * @returns
 */
function makeIndentationDecorators(view) {
	/** @type {RangeSetBuilder<Decoration>} */
	const builder = new RangeSetBuilder();
	const tabSize = Number(view.state.tabSize);
	const { doc } = view.state;
	const spaceOnlyLines = [];
	let currentIndent = 0;
	for (const { from: visibleFrom, to: visibleTo } of view.visibleRanges) {
		let to = visibleFrom - 1;
		let pos; let from; let length; let
			text;
		// eslint-disable-next-line no-cond-assign
		while ((pos = to + 1) <= visibleTo) {
			({
				from, to, length, text,
			} = doc.lineAt(pos));
			const codeStartsAt = getCodeStart(text);
			const isAllSpaces = codeStartsAt === length;
			// we don't have indentation guides for the zero/first indentation level
			const skipIndent = codeStartsAt <= tabSize;
			const isComment = text[codeStartsAt] === '#'; /** @todo for other languages */
			if (isAllSpaces) {
				spaceOnlyLines.push({ from, to });
				// eslint-disable-next-line no-continue
				continue;
			}
			else if (skipIndent) {
				spaceOnlyLines.length = 0;
				// eslint-disable-next-line no-continue
				continue;
			}
			// we have a valid indentation that needs guiding!
			// fill all space only lines that we've kept track of
			const indent = codeStartsAt - (codeStartsAt % tabSize);
			if (!isComment) {
				currentIndent = indent;
			}
			// eslint-disable-next-line no-shadow
			for (const { from, to } of spaceOnlyLines) {
				makeIndentationMark(from, to, currentIndent, tabSize, builder);
				makeIndentationWidget(from, to, currentIndent, tabSize, builder);
			}
			spaceOnlyLines.length = 0;
			makeIndentationMark(from, to, indent, tabSize, builder);
		}
	}
	return builder.finish();
}

const showIndentations = ViewPlugin.fromClass(
	class {
		/** @type {import("@codemirror/view").DecorationSet} */
		decorations;

		/** @type {EditorView} */
		constructor(view) {
			this.decorations = makeIndentationDecorators(view);
		}

		/**
		 *
		 * @param {ViewUpdate} update
		 */
		update(update) {
			if (update.docChanged || update.viewportChanged) {
				this.decorations = makeIndentationDecorators(update.view);
			}
		}
	},
	{
		decorations: v => v.decorations,
	},
);

const indentationTheme = EditorView.baseTheme({
	'.cm-indentation-widget': {
		display: 'inline-block',
	},
	'.cm-indentation-guide': {
		position: 'relative',
		height: '100%',
		display: 'inline-block',
	},
	'.cm-indentation-guide:after': {
		position: 'absolute',
		content: "''",
		right: '12px',
		height: '100%',
		borderLeft: '1px solid rgba(193, 199, 249, 0.4)',
	},
});

/**
 *
 */
export function indentationGuides() {
	return [showIndentations, indentationTheme];
}
